import { useEffect, useRef, useState } from 'react';
import {
  type FilteredRecordReview,
  FilteredRecordReviewsDocument,
  type FilteredRecordReviewsQuery,
  type FilteredRecordReviewsQueryVariables,
} from '@/graphql/gql/graphql';
import { NetworkStatus, useLazyQuery } from '@/libs';
import { useWeeklySqlFilters } from '@/store';
import { FilteredRecordReviewsTable } from './components/filtered-record-reviews-table';
import type { WeeklyRecordsFilters } from './components/generation-form';
import { GenerationForm } from './components/generation-form';

export const WeeklySqlReviewPage = () => {
  /**
   * Filters used for the filteredRecordReviews query. Initialized once from the persisted store on load;
   * afterwards only updated when the user clicks Generate.
   */
  const [submittedFilters, setSubmittedFilters] = useState<WeeklyRecordsFilters | null>(null);
  const [page, setPage] = useState(1);
  const [hasGenerated, setHasGenerated] = useState(false);
  const hasInitializedFromStore = useRef(false);

  const [fetchFilteredRecordReviews, { data, loading, networkStatus }] = useLazyQuery<
    FilteredRecordReviewsQuery,
    FilteredRecordReviewsQueryVariables
  >(FilteredRecordReviewsDocument, {
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (hasInitializedFromStore.current) return;
    hasInitializedFromStore.current = true;
    const state = useWeeklySqlFilters.getState();
    setSubmittedFilters({
      startDate: state.startDate,
      endDate: state.endDate,
      dislikesThreshold: state.dislikesThreshold ?? undefined,
      percentageLessThan: state.percentageLessThan ?? undefined,
      percentageGreaterThan: state.percentageGreaterThan ?? undefined,
    });
  }, []);

  const handleGenerate = (filters: WeeklyRecordsFilters) => {
    setSubmittedFilters(filters);
    setHasGenerated(true);
    setPage(1);

    void fetchFilteredRecordReviews({
      variables: {
        options: {
          page: 1,
          take: 100,
          startDate: filters.startDate ?? undefined,
          endDate: filters.endDate ?? undefined,
          dislikesThreshold: filters.dislikesThreshold ?? undefined,
          percentageLessThan: filters.percentageLessThan ?? undefined,
          percentageGreaterThan: filters.percentageGreaterThan ?? undefined,
        },
      },
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    if (!submittedFilters) return;

    void fetchFilteredRecordReviews({
      variables: {
        options: {
          page: nextPage,
          take: 50,
          startDate: submittedFilters.startDate ?? undefined,
          endDate: submittedFilters.endDate ?? undefined,
          dislikesThreshold: submittedFilters.dislikesThreshold ?? undefined,
          percentageLessThan: submittedFilters.percentageLessThan ?? undefined,
          percentageGreaterThan: submittedFilters.percentageGreaterThan ?? undefined,
        },
      },
    });
  };

  const handleRefetch = () => {
    if (!submittedFilters) return;

    void fetchFilteredRecordReviews({
      variables: {
        options: {
          page,
          take: 50,
          startDate: submittedFilters.startDate ?? undefined,
          endDate: submittedFilters.endDate ?? undefined,
          dislikesThreshold: submittedFilters.dislikesThreshold ?? undefined,
          percentageLessThan: submittedFilters.percentageLessThan ?? undefined,
          percentageGreaterThan: submittedFilters.percentageGreaterThan ?? undefined,
        },
      },
    });
  };

  const isLoading =
    loading ||
    (hasGenerated && !data) ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;

  const rows = (data?.restricted?.filteredRecordReviews?.result ?? []) as FilteredRecordReview[];
  const total = data?.restricted?.filteredRecordReviews?.total ?? 0;

  const skipQuery = !hasGenerated;

  return (
    <div className='flex flex-col h-full gap-4'>
      <GenerationForm onGenerate={handleGenerate} />
      <FilteredRecordReviewsTable
        filters={submittedFilters}
        skipQuery={skipQuery}
        rows={rows}
        total={total}
        page={page}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        onRefetch={handleRefetch}
        title='Weekly SQL Search'
      />
    </div>
  );
};
